/**
 * TagSelectiveDialog — picker for which players to apply a tag to.
 *
 * Opened by useTags.openSelectiveDialog (double-click on a TagChip).
 * Mirrors the open dialog block from EstadisticasNew.js (the version with
 * checkboxes and a "select all" button) but restyled with the v2 theme.
 */

import React from 'react';
import {
  Dialog, DialogTitle, DialogContent, DialogActions, Button,
  Box, Typography, List, ListItemButton, Checkbox, Avatar,
} from '@mui/material';
import { useStatsTheme, withAlpha } from '../../theme/statsTheme';

export default function TagSelectiveDialog({
  open,
  tag,
  players = [],
  selectedPlayerIds = [],
  onTogglePlayer,
  onSelectAll,
  onConfirm,
  onClose,
  t,
}) {
  const theme = useStatsTheme();
  const tt = (key, opts) => {
    if (typeof t === 'function') {
      return t(`estadisticasNew.${key}`, opts);
    }
    return (opts && opts.defaultValue) || key;
  };

  if (!tag) return null;

  const allIds = players.map((p) => p.id);
  const allSelected = allIds.length > 0 && selectedPlayerIds.length === allIds.length;

  return (
    <Dialog
      open={open}
      onClose={onClose}
      maxWidth="sm"
      fullWidth
      PaperProps={{
        sx: {
          backgroundColor: theme.surfaces.elevated,
          color: theme.text.primary,
          border: `1px solid ${theme.accentBorder}`,
          borderRadius: theme.radius.xl,
        },
      }}
    >
      <DialogTitle sx={{ fontWeight: 700, pb: 0.5 }}>
        {tt('applyTag', { tag: tag.libelle, defaultValue: `Apply tag: ${tag.libelle}` })}
      </DialogTitle>
      <DialogContent>
        <Typography sx={{ fontSize: 12, color: theme.text.secondary, mb: 1 }}>
          {tt('selectPlayersOnCourt', { defaultValue: 'Select the players on the floor:' })}
        </Typography>

        <Box sx={{ display: 'flex', justifyContent: 'flex-end', mb: 0.5 }}>
          <Button
            size="small"
            onClick={() => typeof onSelectAll === 'function' && onSelectAll(allIds)}
            sx={{ color: theme.accent, fontSize: 11 }}
          >
            {allSelected
              ? tt('deselectAll', { defaultValue: 'Deselect all' })
              : tt('selectAllOnCourt', { defaultValue: 'Select all' })}
          </Button>
        </Box>

        <List
          sx={{
            maxHeight: 320,
            overflowY: 'auto',
            border: `1px solid ${theme.surfaces.border}`,
            borderRadius: theme.radius.md,
            backgroundColor: theme.surfaces.base,
            p: 0.5,
          }}
        >
          {players.length === 0 && (
            <Typography sx={{ p: 2, fontSize: 12, color: theme.text.muted, fontStyle: 'italic', textAlign: 'center' }}>
              {tt('noActivePlayers', { defaultValue: 'No active players' })}
            </Typography>
          )}
          {players.map((player) => {
            const selected = selectedPlayerIds.includes(player.id);
            return (
              <ListItemButton
                key={player.id}
                onClick={() => typeof onTogglePlayer === 'function' && onTogglePlayer(player.id)}
                sx={{
                  borderRadius: theme.radius.md,
                  mb: 0.4,
                  backgroundColor: selected ? withAlpha(theme.accent, 0.16) : 'transparent',
                  border: `1px solid ${selected ? theme.accentBorder : 'transparent'}`,
                  '&:hover': {
                    backgroundColor: selected ? withAlpha(theme.accent, 0.22) : withAlpha(theme.text.muted, 0.08),
                  },
                }}
              >
                <Checkbox
                  edge="start"
                  checked={selected}
                  tabIndex={-1}
                  disableRipple
                  sx={{
                    color: theme.text.muted,
                    '&.Mui-checked': { color: theme.accent },
                    p: 0.5,
                    mr: 1,
                  }}
                />
                <Avatar
                  src={player.avatarUrl}
                  sx={{
                    width: 28,
                    height: 28,
                    mr: 1,
                    border: `1px solid ${theme.surfaces.border}`,
                    backgroundColor: theme.surfaces.elevated,
                    fontSize: 11,
                  }}
                >
                  {player.nom?.charAt(0).toUpperCase() || '?'}
                </Avatar>
                {player.dorsal && (
                  <Typography
                    sx={{
                      fontSize: 11,
                      fontWeight: 800,
                      color: theme.accent,
                      mr: 0.6,
                      letterSpacing: '0.04em',
                    }}
                  >
                    #{player.dorsal}
                  </Typography>
                )}
                <Typography sx={{ fontSize: 13, fontWeight: 600, color: theme.text.primary }}>
                  {player.nom}
                </Typography>
              </ListItemButton>
            );
          })}
        </List>

        {selectedPlayerIds.length > 0 && (
          <Typography sx={{ fontSize: 11, color: theme.text.secondary, mt: 1, textAlign: 'right' }}>
            {tt('tagWillBeApplied', {
              tag: tag.libelle,
              count: selectedPlayerIds.length,
              defaultValue: `Tag "${tag.libelle}" will apply to ${selectedPlayerIds.length} player(s)`,
            })}
          </Typography>
        )}
      </DialogContent>
      <DialogActions sx={{ px: 3, pb: 2 }}>
        <Button onClick={onClose} sx={{ color: theme.text.secondary }}>
          {tt('cancel', { defaultValue: 'Cancel' })}
        </Button>
        <Button
          onClick={onConfirm}
          variant="contained"
          disabled={selectedPlayerIds.length === 0}
          sx={{
            backgroundColor: theme.accent,
            '&:hover': { backgroundColor: theme.accentStrong },
          }}
        >
          {tt('applyTagButton', { defaultValue: 'Apply tag' })}
        </Button>
      </DialogActions>
    </Dialog>
  );
}
